import React, { useState, useContext } from 'react';
import { Typography, TextField, Button, Container, Card, Box, Link, Divider, Alert } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import '../styles/Login.css';
import { useAuth } from '../context/AuthContext';
import { UserContext } from '../context/UserContext';
import googleIcon from '../assets/google1.png';
import linkedinLogo from '../assets/linkedin-logo.png';
import backgroundLogin from '../assets/background_login.png';
import supabaseClient from '../supabaseClient';
import logo0 from '../assets/logo0.png';
import backgroundImage from '../assets/backgroundlogin.png';
import GoogleIcon from '@mui/icons-material/Google';

const Login = () => {
  const { signIn } = useAuth();
  const { setUserProfile } = useContext(UserContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      // 1. Sign in with Supabase auth
      const { data: { user }, error: signInError } = await supabaseClient.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw signInError;

      if (user) {
        // 2. Get user profile using email match
        const { data: profile, error: profileError } = await supabaseClient
          .from('profiles')
          .select('*')
          .eq('email', formData.email)
          .limit(1)
          .maybeSingle();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          throw profileError;
        }

        // 3. Store user data
        localStorage.setItem('user', JSON.stringify({
          ...user,
          profile: profile || {} // Provide default empty object if no profile
        }));

        // 4. Redirect to dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabaseClient.auth.signOut();
      if (error) throw error;
      
      setUserProfile(null);
      localStorage.removeItem('isAuthenticated');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      setError('Error during logout. Please try again.');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabaseClient.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google sign in error:', error);
      setError('Failed to sign in with Google');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* Header */}
      <Box 
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: { xs: '15px', sm: '20px' },
          borderBottom: '1px solid #333333',
          backgroundColor: '#000000',
          position: 'sticky',
          top: 0,
          zIndex: 2
        }}
      >
        <Box 
          sx={{
            maxWidth: '800px',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Link to="/" style={{ textDecoration: 'none' }}>
            <img 
              src={logo0} 
              alt="Domain Factory" 
              style={{ 
                height: '30px',
                filter: 'brightness(0) invert(1)'
              }}
            />
          </Link>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ 
        py: { xs: 3, sm: 4 },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Card 
          sx={{ 
            padding: { xs: '24px', sm: '32px' },
            maxWidth: '800px',
            width: '100%',
            margin: '0 auto',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '32px', sm: '40px' },
            position: 'relative',
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h6" 
              sx={{
                mb: 3,
                color: '#333',
                fontWeight: 500
              }}
            >
              E-Mail / Username
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                type="email"
                variant="outlined"
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    backgroundColor: '#ffffff'
                  }
                }}
              />

              <Typography 
                variant="h6" 
                sx={{
                  mb: 3,
                  color: '#333',
                  fontWeight: 500
                }}
              >
                Password
              </Typography>

              <TextField
                type="password"
                variant="outlined"
                fullWidth
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    backgroundColor: '#ffffff'
                  }
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ 
                  borderRadius: 0,
                  textTransform: 'none',
                  py: 1.5,
                  backgroundColor: '#008945',
                  fontSize: '16px',
                  fontWeight: 500,
                  mb: 3,
                  '&:hover': {
                    backgroundColor: '#007538'
                  }
                }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Login'}
              </Button>

              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  color: '#008945',
                  textDecoration: 'none',
                  fontSize: '14px',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Need help logging in?
              </Link>
            </form>
          </Box>

          <Box sx={{
            flex: 1,
            backgroundColor: '#f8f9fa',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography variant="h6" sx={{
              color: '#333',
              fontWeight: 500
            }}>
              Not a customer yet?
            </Typography>
            
            <Typography sx={{
              color: '#666',
              mb: 2
            }}>
              Create your customer account here quickly and easily.
            </Typography>

            <Button
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                py: 1.5,
                borderColor: '#008945',
                color: '#008945',
                fontSize: '16px',
                '&:hover': {
                  borderColor: '#007538',
                  backgroundColor: 'rgba(0, 137, 69, 0.04)'
                }
              }}
              onClick={() => navigate('/join')}
            >
              Create Customer Account
            </Button>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              my: 2
            }}>
              <Divider sx={{ flex: 1 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  px: 2,
                  color: '#666'
                }}
              >
                or
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Google Sign In Button with custom icon */}
            <Button
              variant="outlined"
              fullWidth
              startIcon={
                <Box
                  component="img"
                  src={googleIcon}
                  alt="Google"
                  sx={{
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain',
                    mr: 1
                  }}
                />
              }
              onClick={handleGoogleSignIn}
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                py: 1.5,
                px: 2,
                borderColor: '#dadce0',
                color: '#3c4043',
                fontSize: '16px',
                fontWeight: 500,
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#4285f4',
                  backgroundColor: '#f8f9fa',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 1px 4px rgba(66, 133, 244, 0.2)'
                }
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontWeight: 500,
                  letterSpacing: '0.25px'
                }}
              >
                Continue with Google
              </Typography>
            </Button>
          </Box>
        </Card>
      </Container>

      {/* Footer */}
      <Box 
        component="footer"
        sx={{
          width: '100%',
          backgroundColor: '#333',
          color: '#fff',
          mt: 'auto'
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{
            py: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 2, sm: 3 },
            textAlign: 'center'
          }}
        >
          <Link 
            component="a"
            href="#" 
            sx={{ 
              color: '#fff', 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Impressum
          </Link>
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              backgroundColor: '#666',
              display: { xs: 'none', sm: 'block' } 
            }} 
          />
          <Link 
            component="a"
            href="#" 
            sx={{ 
              color: '#fff', 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Datenschutz
          </Link>
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              backgroundColor: '#666',
              display: { xs: 'none', sm: 'block' } 
            }} 
          />
          <Link 
            component="a"
            href="#" 
            sx={{ 
              color: '#fff', 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Widerrufsrecht
          </Link>
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              backgroundColor: '#666',
              display: { xs: 'none', sm: 'block' } 
            }} 
          />
          <Typography sx={{ color: '#666' }}>
            © 2025 DomainFactory GmbH
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
